package info.rezident.app.ui.components.sections

import androidx.compose.runtime.*
import androidx.compose.web.events.SyntheticMouseEvent
import com.varabyte.kobweb.compose.css.functions.blur
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.icons.CloseIcon
import com.varabyte.kobweb.silk.components.icons.HamburgerIcon
import com.varabyte.kobweb.silk.components.icons.MoonIcon
import com.varabyte.kobweb.silk.components.icons.SunIcon
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.components.overlay.OverlayVars
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import info.rezident.app.MaxWidthStyle
import info.rezident.app.core.AppTracking
import info.rezident.app.data.auth.AuthRepository
import info.rezident.app.ui.components.widgets.IconButton
import info.rezident.app.ui.theme.AppTheme
import org.jetbrains.compose.web.css.*

@Composable
private fun ColorModeButton() {
    var colorMode by ColorMode.currentState
    IconButton(onClick = {
        colorMode = colorMode.opposite

        AppTracking.logEvent(
            "dark_mode_switch",
            mapOf("dark_mode_new_state" to colorMode.isDark)
        )
    }) {
        if (colorMode.isLight)
            MoonIcon(Modifier.color(AppTheme.colors.text))
        else
            SunIcon(Modifier.color(AppTheme.colors.text))
    }
}

@Composable
private fun HamburgerButton(modifier: Modifier = Modifier, onClick: () -> Unit) {
    IconButton(modifier, onClick) {
        HamburgerIcon()
    }
}

@Composable
private fun CloseButton(modifier: Modifier = Modifier, onClick: () -> Unit) {
    IconButton(modifier, onClick) {
        CloseIcon()
    }
}

val SideMenuSlideInAnim = Keyframes {
    from {
        Modifier.translateX(100.percent)
    }

    to {
        Modifier
    }
}

// Note: When the user closes the side menu, we don't immediately stop rendering it (at which point it would disappear
// abruptly). Instead, we start animating it out and only stop rendering it when the animation is complete.
enum class SideMenuState {
    CLOSED,
    OPEN,
    CLOSING;

    fun close() = when (this) {
        CLOSED -> CLOSED
        OPEN -> CLOSING
        CLOSING -> CLOSING
    }
}

val headerPaddingStyle = CssStyle {
    base {
        Modifier.padding {
            left(14.px)
            right(14.px)
            topBottom(5.px)
        }
    }

    Breakpoint.MD {
        Modifier.padding(leftRight = 16.px, topBottom = 10.px)
    }

    Breakpoint.XL {
        Modifier.padding(topBottom = 10.px)
    }
}

val headerLogoStyle = CssStyle {
    base {
        Modifier.height(60.px)
    }

    Breakpoint.SM {
        Modifier.height(65.px)
    }

    Breakpoint.MD {
        Modifier.height(70.px)
    }

    Breakpoint.LG {
        Modifier.height(90.px)
    }
}

@Composable
fun NavHeader(modifier: Modifier = Modifier, isSimple: Boolean = false) {
    Box(
        modifier
            .fillMaxWidth()
            .backgroundColor(AppTheme.colors.header)
            .backdropFilter(blur(4.px))
            .zIndex(1000)
            .position(Position.Fixed)
            .left(0.percent)
            .top(0.percent)
            .right(0.percent)
            .styleModifier {
                property("margin", "auto")
            },
        contentAlignment = Alignment.TopCenter
    ) {
        Row(
            MaxWidthStyle.toModifier()
                .then(headerPaddingStyle.toModifier())
                .fillMaxWidth(),
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Link("/") {
                val colorMode = ColorMode.current
                Image(
                    if (colorMode.isLight) {
                        "/logo-light.png"
                    } else {
                        "/logo-dark.png"
                    },
                    "rezident.info Logo",
                    headerLogoStyle.toModifier()
                        .display(DisplayStyle.Block)
                )
            }

            Spacer()

            if (isSimple) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(20.px),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    ColorModeButton()
                }
            } else {
                var menuState by remember { mutableStateOf(SideMenuState.CLOSED) }
                HamburgerButton(
                    Modifier.displayUntil(Breakpoint.MD),
                    onClick = { menuState = SideMenuState.OPEN }
                )

                if (menuState != SideMenuState.CLOSED) {
                    SideMenu(
                        menuState,
                        close = { menuState = menuState.close() },
                        onAnimationEnd = {
                            if (menuState == SideMenuState.CLOSING)
                                menuState = SideMenuState.CLOSED
                        }
                    )
                }

                val pageContext = rememberPageContext()
                NavHeaderButtons(
                    Modifier.displayIfAtLeast(Breakpoint.MD),
                    onQuizClick = {
                        AppTracking.logEvent(
                            AppTracking.EVENT_LANDING_CTA_CLICK,
                            mapOf(AppTracking.PARAM_CTA_LOCATION to AppTracking.CTA_LOCATION_MENU)
                        )
                        pageContext.router.navigateTo("/quiz")
                    }
                )
            }
        }
    }
}

@Composable
private fun SideMenu(menuState: SideMenuState, close: () -> Unit, onAnimationEnd: () -> Unit) {
    Overlay(
        Modifier
            .setVariable(OverlayVars.BackgroundColor, Colors.Transparent)
            .onClick { close() }
            .zIndex(1001)
    ) {
        key(menuState) { // Force recompute animation parameters when close button is clicked
            Column(
                Modifier
                    .fillMaxSize()
                    .align(Alignment.CenterEnd)
                    // Close button will appear roughly over the hamburger button, so the user can close
                    // things without moving their finger / cursor much.
                    .padding(top = 1.cssRem, leftRight = 1.cssRem)
                    .gap(1.5.cssRem)
                    .backgroundColor(AppTheme.colors.background)
                    .animation(
                        SideMenuSlideInAnim.toAnimation(
                            duration = 200.ms,
                            timingFunction = if (menuState == SideMenuState.OPEN) {
                                AnimationTimingFunction.EaseOut
                            } else {
                                AnimationTimingFunction.EaseIn
                            },
                            direction = if (menuState == SideMenuState.OPEN) {
                                AnimationDirection.Normal
                            } else {
                                AnimationDirection.Reverse
                            },
                            fillMode = AnimationFillMode.Forwards
                        )
                    )
                    .onClick { it.stopPropagation() }
                    .onAnimationEnd { onAnimationEnd() },
                horizontalAlignment = Alignment.End
            ) {
                CloseButton(onClick = { close() })

                SideMenuButtons(closeSideMenu = close)
            }
        }
    }
}

@Composable
private fun SideMenuButtons(closeSideMenu: () -> Unit) {
    Column(
        Modifier
            .fillMaxSize()
            .padding(right = 12.px, top = 24.px, bottom = 24.px)
            .gap(24.px),
        horizontalAlignment = Alignment.End
    ) {
        HomeButton(Modifier, closeSideMenu)

        HowToButton(Modifier, closeSideMenu)

        ContactButton()

        val pageContext = rememberPageContext()
        LogInButton { _ -> closeSideMenu() }

        QuizButton {
            AppTracking.logEvent(
                AppTracking.EVENT_LANDING_CTA_CLICK,
                mapOf(AppTracking.PARAM_CTA_LOCATION to AppTracking.CTA_LOCATION_MENU)
            )
            pageContext.router.navigateTo("/quiz")
        }

        Spacer()

        ColorModeButton()
    }
}

@Composable
private fun HomeButton(
    modifier: Modifier = Modifier,
    onClick: () -> Unit = {}
) {
    Link(
        "/#acasa",
        "Acasă",
        modifier
            .then(AppTheme.typography.body1.toModifier())
            .color(AppTheme.colors.text)
            .onClick {
                AppTracking.logEvent(AppTracking.EVENT_NAV_HOME_CLICK)
                onClick()
            },
    )
}

@Composable
private fun HowToButton(
    modifier: Modifier = Modifier,
    onClick: () -> Unit = {}
) {
    Link(
        "/#cum-functioneaza",
        "Cum funcționează",
        modifier
            .then(AppTheme.typography.body1.toModifier())
            .color(AppTheme.colors.text)
            .onClick {
                AppTracking.logEvent(AppTracking.EVENT_NAV_HOW_IT_WORKS_CLICK)
                onClick()
            },
    )
}

@Composable
private fun ContactButton() {
    Link(
        "/contact",
        "Contact",
        AppTheme.typography.body1.toModifier()
            .color(AppTheme.colors.text)
            .onClick { AppTracking.logEvent(AppTracking.EVENT_NAV_CONTACT_CLICK) },
    )
}

@Composable
private fun LogInButton(extraOnClick: ((SyntheticMouseEvent) -> Unit)? = null) {
    val pageContext = rememberPageContext()
    val userState by AuthRepository.firebaseUser.collectAsState(initial = null)
    val isSignedInAndNotAnonymous by derivedStateOf {
        userState?.isAnonymous == false
    }

    Button(
        onClick = { evt ->
            extraOnClick?.invoke(evt)
            if (isSignedInAndNotAnonymous) {
                pageContext.router.navigateTo("/profil")
            } else {
                AppTracking.logEvent(AppTracking.EVENT_LANDING_CONNECT_CLICK)
                pageContext.router.navigateTo("/conectare")
            }
        },
        modifier = Modifier
            .backgroundColor(Colors.Transparent)
            .border(1.px, LineStyle.Solid, AppTheme.colors.text)
            .borderRadius(AppTheme.shapes.mediumRound),
        size = ButtonSize.MD
    ) {
        val label = if (isSignedInAndNotAnonymous) {
            "Recomandări"
        } else {
            "Profilul Meu"
        }
        SpanText(
            label,
            AppTheme.typography.body1Medium.toModifier()
                .color(AppTheme.colors.text)
        )
    }
}

@Composable
private fun QuizButton(onClick: (SyntheticMouseEvent) -> Unit) {
    Button(
        onClick = onClick,
        modifier = Modifier
            .setVariable(ButtonVars.BackgroundDefaultColor, AppTheme.colors.accent)
            .borderRadius(AppTheme.shapes.mediumRound),
        size = ButtonSize.MD,
        colorPalette = AppTheme.colors.accentColorPalette
    ) {
        SpanText(
            "Încearcă acum",
            AppTheme.typography.body1Medium.toModifier()
                .color(AppTheme.colors.onAccent)
        )
    }
}

@Composable
private fun NavHeaderButtons(
    modifier: Modifier = Modifier,
    onQuizClick: () -> Unit
) {
    Row(
        modifier.gap(20.px),
        verticalAlignment = Alignment.CenterVertically
    ) {
        HomeButton()

        HowToButton(Modifier.displayIfAtLeast(Breakpoint.LG))

        ContactButton()

        ColorModeButton()

        LogInButton()

        QuizButton {
            onQuizClick()
        }
    }
}